import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { useTranslation } from 'react-i18next';
import { ACTIONS, UI, useAbility } from 'permission';
import { Col, Form, Radio, Row } from 'antd';
import { Card, Input, Select, Button, BUTTON_TYPES, notification, NOTIFICATION_TYPES, Popup, Status } from '_atoms';
import { BreadCrumbs } from '_atoms/BreadCrumbs';
import { SELECT_TYPES } from '_atoms/Select/constants';
import type { SelectOption } from '_atoms/Select/types';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { PromotionRequestMainInfo } from '_molecules/PromotionRequestMainInfo';
import history from 'helpers/history';
import {
  copyPromotionRequest,
  createPromotionRequest,
  getJobBoards,
  getJobInfo,
  getOpeningById,
  getOpeningsList,
  getPromotionRequest,
  getTechnologyAreas,
  updatePromotionRequest,
} from 'actions';
import { ROUTES } from 'router/constants';
import { OPENING_STATUSES } from 'constants/opening';
import {
  PAID_SOCIAL_MEDIA_PLATFORMS_OPTIONS,
  PROMOTION_PERIOD_TYPES,
  PROMOTION_REQUEST_COPY_STATUSES,
  PROMOTION_REQUEST_FULL_EDIT_STATUSES,
  PROMOTION_REQUEST_STATUS_TYPES,
  PROMOTION_SOURCE_OPTIONS,
  PROMOTION_SOURCES,
  durationOptions,
} from 'constants/promotionRequest';
import { ReactComponent as Union } from 'dist/icons/Union.svg';
import { ReactComponent as Bin } from 'dist/icons/bin.svg';
import type { PromotionRequest } from 'store/reducers/promotionRequestReducer';
import { EMPTY_SOURCE, INITIAL_VALUES } from './constants';
import {
  applySourceChangeDefaults,
  formValuesToPayloads,
  mergeSelectOptions,
  officeOptions,
  openingOptions,
  priorOpeningPrefill,
  promotionRequestToFormValues,
  requestOpeningOptions,
  teamLeadOptions,
  userOptions,
} from './utils';
import './style.scss';

export const CreatePromotionRequest = () => {
  const { t } = useTranslation();
  const { id, requestId } = useParams<{ id: string; requestId?: string }>();
  const { state: locationState } = useLocation<{ jobName?: string }>();
  const isCopy = !!useRouteMatch(ROUTES.JOBS_PROMOTION_REQUEST_COPY);
  const [form] = Form.useForm();
  const watchedSourceRows: { promotion_source?: SelectOption }[] | undefined = Form.useWatch('sources', form);
  const hasJobBoardsSource = !!watchedSourceRows?.some(
    source => source?.promotion_source?.value === PROMOTION_SOURCES.JOB_BOARDS
  );
  const dispatch = useAppDispatch();
  const ability = useAbility();

  const isEdit = !!requestId && !isCopy;
  const isCreate = !isEdit && !isCopy;
  const needsRequest = isEdit || isCopy;

  const [loading, setLoading] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [jobName, setJobName] = useState(locationState?.jobName ?? null);
  const [openings, setOpenings] = useState([]);
  const [technologyAreas, setTechnologyAreas] = useState([]);
  const [searchRegionOptions, setSearchRegionOptions] = useState([]);
  const [recruiterLeadOptions, setRecruiterLeadOptions] = useState([]);
  const [teamLeadList, setTeamLeadList] = useState([]);
  const [jobBoards, setJobBoards] = useState<{ results: SelectOption[]; loading: boolean }>({
    results: [],
    loading: false,
  });
  const [request, setRequest] = useState<PromotionRequest | null>(null);
  const [parentSource, setParentSource] = useState<string | undefined>(undefined);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const previousSourcesRef = useRef(INITIAL_VALUES.sources);

  const status = request?.status;
  const isFullEdit = isEdit && !!status && PROMOTION_REQUEST_FULL_EDIT_STATUSES.includes(status);
  const ready = !needsRequest || !!request;

  /* Copy: the parent's promotion source must not be reusable (single request per source). */
  const sourceOptions = useMemo(
    () =>
      isCopy && parentSource
        ? PROMOTION_SOURCE_OPTIONS.filter(option => option.value !== parentSource)
        : PROMOTION_SOURCE_OPTIONS,
    [isCopy, parentSource]
  );

  const requiredRule = useMemo(() => [{ required: true, message: t('translation:validation.required') }], [t]);

  const periodTypeLabels = useMemo(
    () => ({
      [PROMOTION_PERIOD_TYPES.DAY]: t('translation:create_promotion_request.period_day'),
      [PROMOTION_PERIOD_TYPES.MONTH]: t('translation:create_promotion_request.period_month'),
    }),
    [t]
  );

  const periodTypeOptions = useMemo(
    () => Object.entries(periodTypeLabels).map(([value, label]) => ({ value, label })),
    [periodTypeLabels]
  );

  const title = useMemo(() => {
    if (isCopy) return t('translation:create_promotion_request.title_copy');
    if (!isEdit) return t('translation:create_promotion_request.title_creation');
    const prefix = t('translation:create_promotion_request.title_edit');
    return request?.request_title ? `${prefix} — ${request.request_title}` : prefix;
  }, [isCopy, isEdit, request, t]);

  useEffect(() => {
    if (!hasJobBoardsSource) return;

    setJobBoards(prev => ({ ...prev, loading: true }));
    getJobBoards()
      .then(boards => setJobBoards({ results: (boards || []).map(value => ({ value, label: value })), loading: false }))
      .catch(() => setJobBoards(prev => ({ ...prev, loading: false })));
  }, [hasJobBoardsSource]);

  useEffect(() => {
    dispatch(getJobInfo(id, false)).then(resp => {
      if (!resp) return;
      setJobName(resp.name);
      setRecruiterLeadOptions(userOptions(Array.isArray(resp.recruiter_leads) ? resp.recruiter_leads : []));
      setTeamLeadList(teamLeadOptions(resp));
    });
    getOpeningsList(id, { status: OPENING_STATUSES.OPEN })
      .then(resp => setOpenings(prev => mergeSelectOptions(openingOptions(resp || []), prev)))
      .catch(() => {});
    getTechnologyAreas(id)
      .then(resp =>
        setTechnologyAreas(
          (resp || []).filter(area => !area.is_outdated).map(area => ({ value: area.uuid, label: area.name }))
        )
      )
      .catch(() => {});
    if (requestId) {
      getPromotionRequest(id, requestId)
        .then(resp => {
          if (!resp) return;
          const invalidEdit =
            isEdit &&
            (!PROMOTION_REQUEST_FULL_EDIT_STATUSES.includes(resp.status) ||
              !ability.can(ACTIONS.UPDATE, UI.PROMOTION_REQUEST_EDIT));
          const invalidCopy = isCopy && !PROMOTION_REQUEST_COPY_STATUSES.includes(resp.status);
          if (invalidEdit || invalidCopy) {
            history.replace(`/jobs/${id}/promotion-request/${requestId}`);
            return;
          }
          setRequest(resp);
          if (resp.job?.name) setJobName(resp.job.name);
          setOpenings(prev => mergeSelectOptions(prev, requestOpeningOptions(resp)));
          const prefill = promotionRequestToFormValues(resp, periodTypeLabels);
          if (isCopy) {
            setParentSource(resp.promotion_source);
            prefill.sources = prefill.sources.map(source => ({ ...source, ...EMPTY_SOURCE }));
          }
          form.setFieldsValue(prefill);
          previousSourcesRef.current = prefill.sources;
          setHasUnsavedChanges(false);
          setSearchRegionOptions(officeOptions(resp.search_regions || []));
          const prior = resp.prior_opening?.uuid;
          if (prior) {
            getOpeningById(id, prior)
              .then(opening => opening && setSearchRegionOptions(officeOptions(opening.offices || [])))
              .catch(() => {});
          }
        })
        .catch(() => {});
    }
  }, []);

  const runPrefill = (priorUuid, withValues = !isEdit) => {
    if (!priorUuid || jobName === null) return;
    getOpeningById(id, priorUuid)
      .then(opening => {
        if (!opening) return;
        const { fields, officeOptions: regionOptions } = priorOpeningPrefill(opening, jobName);
        if (withValues) form.setFields(fields);
        setSearchRegionOptions(regionOptions);
      })
      .catch(() => {});
  };

  const onOpeningsChange = () => {
    const rows = form.getFieldValue('openings') || [];
    const selectedUuids = rows.map(row => row?.opening?.value).filter(Boolean);
    let prior = form.getFieldValue('prior_opening');
    if (!selectedUuids.includes(prior)) {
      [prior] = selectedUuids;
      form.setFieldValue('prior_opening', prior);
    }
    if (prior) {
      runPrefill(prior);
    } else if (!isEdit) {
      setSearchRegionOptions([]);
      form.setFields([
        { name: 'request_title', value: '' },
        { name: 'description', value: '' },
        { name: 'description_initial', value: '' },
        { name: 'search_regions', value: [] },
        { name: 'technology_areas', value: [] },
      ]);
    }
  };

  const applySourceDefaults = changedSources => {
    const { sources, mutated } = applySourceChangeDefaults({
      currentSources: form.getFieldValue('sources') || [],
      previousSources: previousSourcesRef.current || [],
      changedSources,
      periodTypeLabels,
    });
    if (mutated) form.setFieldValue('sources', sources);
    previousSourcesRef.current = form.getFieldValue('sources');
  };

  const onValuesChange = changed => {
    setHasUnsavedChanges(true);
    if (changed.sources) applySourceDefaults(changed.sources);
    if ('openings' in changed) onOpeningsChange();
  };

  const exitForm = (requestData = null) => {
    if (isCopy) {
      history.push(`/jobs/${id}/promotion-request/${requestId}`);
      return;
    }
    if (!isEdit) {
      history.goBack();
      return;
    }
    history.push({
      pathname: `/jobs/${id}/promotion-request/${requestId}`,
      state: requestData ? { request: requestData } : undefined,
    });
  };

  const onCloseConfirmation = () => setOpenConfirmation(false);

  const onGoBack = () => {
    const { description, additional_requirements } = form.getFieldsValue();
    if (
      description !== form.getFieldValue('description_initial') ||
      additional_requirements !== form.getFieldValue('additional_requirements_initial') ||
      hasUnsavedChanges
    ) {
      setOpenConfirmation(true);
    } else {
      exitForm(request);
    }
  };

  const onUpdate = values => {
    const body = formValuesToPayloads(values)[0];
    setLoading(true);
    updatePromotionRequest(id, requestId, body)
      .then(resp => {
        if (resp) {
          notification({
            type: NOTIFICATION_TYPES.SUCCESS,
            message: t('translation:create_promotion_request.success_updated'),
          });
          exitForm();
        } else {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:create_promotion_request.error_updated'),
          });
        }
      })
      .finally(() => setLoading(false));
  };

  const onCreate = values => {
    const payloads = formValuesToPayloads(values);
    setLoading(true);
    Promise.allSettled(payloads.map(payload => createPromotionRequest(id, payload)))
      .then(results => {
        const createdRequests = results.reduce<PromotionRequest[]>((requests, result) => {
          if (result.status === 'fulfilled' && result.value) requests.push(result.value);
          return requests;
        }, []);
        if (createdRequests.length === payloads.length) {
          notification({
            type: NOTIFICATION_TYPES.SUCCESS,
            message: t('translation:create_promotion_request.success_created'),
          });
          if (createdRequests.length === 1) {
            history.push(`/jobs/${id}/promotion-request/${createdRequests[0].uuid}`);
          } else {
            history.push(`/jobs/${id}/show/promotion-requests`);
          }
        } else if (createdRequests.length > 0) {
          notification({
            type: NOTIFICATION_TYPES.WARNING,
            message: t('translation:create_promotion_request.partial_created'),
          });
        } else {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:create_promotion_request.error_created'),
          });
        }
      })
      .finally(() => setLoading(false));
  };

  const onCopy = values => {
    const body = formValuesToPayloads(values)[0];
    setLoading(true);
    copyPromotionRequest(id, requestId, body)
      .then(resp => {
        if (resp) {
          notification({
            type: NOTIFICATION_TYPES.SUCCESS,
            message: t('translation:create_promotion_request.success_copied'),
          });
          history.push({
            pathname: `/jobs/${id}/promotion-request/${resp.uuid}`,
            state: { request: resp },
          });
        } else {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:create_promotion_request.error_copied'),
          });
        }
      })
      .finally(() => setLoading(false));
  };

  const onFinish = values => {
    if (isCopy) return onCopy(values);
    return isEdit ? onUpdate(values) : onCreate(values);
  };

  const submitLabel = isEdit
    ? t('translation:create_promotion_request.button_save')
    : t('translation:create_promotion_request.button_create');

  return (
    <div className="wrapper-content promotion-request-creation__wrapper">
      <BreadCrumbs
        path="/jobs"
        element={t('translation:create_promotion_request.breadcrumb_jobs')}
        crumbs={[{ path: `/jobs/${id}/show/promotion-requests`, label: jobName }]}
        elementName={title}
      />
      <Form
        className="promotion-request-creation__form"
        form={form}
        onFinish={onFinish}
        initialValues={INITIAL_VALUES}
        validateTrigger="onSubmit"
        onValuesChange={onValuesChange}
      >
        <div className="promotion-request-creation__header-wrapper">
          <Card className="promotion-request-creation__header">
            <div className="promotion-request-creation__header-title">
              <Typography.Title1>{title}</Typography.Title1>
              {isFullEdit && <Status type={PROMOTION_REQUEST_STATUS_TYPES[status]}>{status}</Status>}
            </div>
            <div className="submit-buttons">
              <Button disabled={loading} onClick={onGoBack}>
                {t('translation:create_promotion_request.button_cancel')}
              </Button>
              <Button htmlType="submit" loading={loading} type={BUTTON_TYPES.PRIMARY}>
                {submitLabel}
              </Button>
            </div>
          </Card>
        </div>

        {ready && (
          <Card className="promotion-request-creation">
            <Typography.Title2 className="promotion-request-creation__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
              {t('translation:create_promotion_request.section_promotion_info')}
            </Typography.Title2>

            <Form.List name="sources">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div className="promotion-request-creation__source" key={field.key}>
                      <Form.Item noStyle shouldUpdate={(prev, curr) => prev.sources !== curr.sources}>
                        {({ getFieldValue }) => {
                          const isJobBoards =
                            getFieldValue(['sources', field.name, 'promotion_source'])?.value ===
                            PROMOTION_SOURCES.JOB_BOARDS;
                          const isPpc =
                            getFieldValue(['sources', field.name, 'promotion_source'])?.value === PROMOTION_SOURCES.PPC;
                          return (
                            <div className="promotion-request-creation__source-main">
                              <Select
                                labelInValue
                                allowClear
                                className="promotion-request-creation__source-field"
                                label={t('translation:create_promotion_request.label_promotion_source')}
                                placeholder={t('translation:create_promotion_request.ph_promotion_source')}
                                options={sourceOptions}
                                itemProps={{
                                  ...field,
                                  name: [field.name, 'promotion_source'],
                                  fieldKey: [field.fieldKey, 'promotion_source'],
                                  rules: requiredRule,
                                }}
                              />
                              {isPpc && (
                                <Select
                                  labelInValue
                                  allowClear
                                  mode={SELECT_TYPES.MULTIPLE}
                                  className="promotion-request-creation__source-field"
                                  label={t('translation:create_promotion_request.label_platform')}
                                  placeholder={t('translation:create_promotion_request.ph_platform')}
                                  options={PAID_SOCIAL_MEDIA_PLATFORMS_OPTIONS}
                                  itemProps={{
                                    ...field,
                                    name: [field.name, 'platform'],
                                    fieldKey: [field.fieldKey, 'platform'],
                                    rules: requiredRule,
                                  }}
                                />
                              )}
                              {isJobBoards && (
                                <Select
                                  labelInValue
                                  allowClear
                                  mode={SELECT_TYPES.MULTIPLE}
                                  className="promotion-request-creation__source-field promotion-request-creation__source-field--boards"
                                  label={t('translation:create_promotion_request.label_job_boards')}
                                  placeholder={t('translation:create_promotion_request.ph_job_boards')}
                                  options={jobBoards.results}
                                  loading={jobBoards.loading}
                                  disabled={jobBoards.loading}
                                  itemProps={{
                                    ...field,
                                    name: [field.name, 'job_boards'],
                                    fieldKey: [field.fieldKey, 'job_boards'],
                                    rules: requiredRule,
                                  }}
                                />
                              )}
                              {index > 0 && (
                                <span className="promotion-request-creation__source-remove">
                                  <Button
                                    type={BUTTON_TYPES.GHOST}
                                    onClick={() => remove(field.name)}
                                    icon={<Bin />}
                                    variant="grey"
                                  />
                                </span>
                              )}
                            </div>
                          );
                        }}
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate={(prev, curr) => prev.sources !== curr.sources}>
                        {({ getFieldValue }) => {
                          const periodType = getFieldValue(['sources', field.name, 'promotion_period_type'])?.value;
                          return (
                            <div className="promotion-request-creation__source-main">
                              <Select
                                labelInValue
                                allowClear
                                className="promotion-request-creation__source-field-sm"
                                label={t('translation:create_promotion_request.label_promotion_period_type')}
                                placeholder={t('translation:create_promotion_request.ph_promotion_period_type')}
                                options={periodTypeOptions}
                                itemProps={{
                                  ...field,
                                  name: [field.name, 'promotion_period_type'],
                                  fieldKey: [field.fieldKey, 'promotion_period_type'],
                                  rules: requiredRule,
                                }}
                              />
                              <Select
                                labelInValue
                                allowClear
                                className="promotion-request-creation__source-field-sm"
                                label={t('translation:create_promotion_request.label_duration')}
                                placeholder={t('translation:create_promotion_request.ph_duration')}
                                options={durationOptions(periodType)}
                                disabled={!periodType}
                                itemProps={{
                                  ...field,
                                  name: [field.name, 'duration'],
                                  fieldKey: [field.fieldKey, 'duration'],
                                  rules: requiredRule,
                                }}
                              />
                            </div>
                          );
                        }}
                      </Form.Item>
                    </div>
                  ))}
                  {isCreate && (
                    <Button
                      className="promotion-request-creation__add-source"
                      icon={<Union />}
                      onClick={() => add({ ...EMPTY_SOURCE })}
                    >
                      {t('translation:create_promotion_request.button_add_source')}
                    </Button>
                  )}
                </>
              )}
            </Form.List>

            <div className="promotion-request-creation__divider" />

            <Typography.Title2 className="promotion-request-creation__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
              {t('translation:create_promotion_request.section_main_info')}
            </Typography.Title2>

            <Form.List name="openings">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <Row key={field.key} className="promotion-request-creation__opening" gutter={[32, 16]}>
                      <Col lg={12} md={12} sm={24}>
                        <Input
                          disabled
                          value={jobName}
                          label={index === 0 ? t('translation:create_promotion_request.label_job') : undefined}
                        />
                      </Col>
                      <Col lg={12} md={12} sm={24}>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, curr) =>
                            prev.openings !== curr.openings || prev.prior_opening !== curr.prior_opening
                          }
                        >
                          {({ getFieldValue, setFieldValue }) => {
                            const rows = getFieldValue('openings') || [];
                            const currentValue = rows[field.name]?.opening?.value;
                            const taken = rows
                              .filter((_, i) => i !== field.name)
                              .map(row => row?.opening?.value)
                              .filter(Boolean);
                            const options = openings.filter(option => !taken.includes(option.value));
                            const isPrior = !!currentValue && getFieldValue('prior_opening') === currentValue;
                            return (
                              <div className="promotion-request-creation__opening-row">
                                <Select
                                  disabled={jobName === null}
                                  labelInValue
                                  allowClear
                                  className="promotion-request-creation__opening-select"
                                  label={
                                    index === 0 ? t('translation:create_promotion_request.label_opening') : undefined
                                  }
                                  placeholder={t('translation:create_promotion_request.ph_opening')}
                                  options={options}
                                  itemProps={{
                                    ...field,
                                    name: [field.name, 'opening'],
                                    fieldKey: [field.fieldKey, 'opening'],
                                    rules: requiredRule,
                                  }}
                                />
                                <Radio
                                  className="promotion-request-creation__prior-radio"
                                  checked={isPrior}
                                  disabled={!currentValue}
                                  onChange={() => {
                                    setFieldValue('prior_opening', currentValue);
                                    setHasUnsavedChanges(true);
                                    runPrefill(currentValue);
                                  }}
                                >
                                  {t('translation:create_promotion_request.label_prior')}
                                </Radio>
                                <span className="promotion-request-creation__opening-remove">
                                  {index > 0 && (
                                    <Button
                                      type={BUTTON_TYPES.GHOST}
                                      onClick={() => {
                                        remove(field.name);
                                        onOpeningsChange();
                                      }}
                                      icon={<Bin />}
                                      variant="grey"
                                    />
                                  )}
                                </span>
                              </div>
                            );
                          }}
                        </Form.Item>
                      </Col>
                    </Row>
                  ))}
                  {fields.length < openings.length && (
                    <Button
                      className="promotion-request-creation__add-opening"
                      icon={<Union />}
                      onClick={() => add({ opening: undefined })}
                    >
                      {t('translation:create_promotion_request.button_add_opening')}
                    </Button>
                  )}
                </>
              )}
            </Form.List>

            <PromotionRequestMainInfo
              requiredRule={requiredRule}
              technologyAreas={technologyAreas}
              searchRegionOptions={searchRegionOptions}
              recruiterLeadOptions={recruiterLeadOptions}
              teamLeadList={teamLeadList}
            />
          </Card>
        )}
      </Form>

      <Popup
        open={openConfirmation}
        title={t('translation:create_promotion_request.title_discard_changes')}
        cancelButtonProps={{
          onClick: onCloseConfirmation,
          children: t('translation:create_promotion_request.button_back_to_page'),
        }}
        okButtonProps={{
          onClick: () => exitForm(request),
          children: t('translation:create_promotion_request.button_discard'),
        }}
      >
        {t('translation:create_promotion_request.text_changes_not_saved')}
      </Popup>
    </div>
  );
};
